import React, { useState, useEffect } from "react";
import { createClient } from "@supabase/supabase-js";
import { useUser } from "../context/UserContext";
import { supabase } from "../supabaseClient";
import CustomSelect from "./common/CustomSelect";
import { useNotification } from "../context/NotificationContext";
import * as XLSX from "xlsx";

function Users({ subTeams = [], onRefreshTeams, onRefreshUsers, darkMode }) {
    const { showAlert } = useNotification();
    const { currentUser, refreshUser } = useUser();
    const isGlobalAdmin = currentUser?.role === "Global Admin";
    const isOperationalAdmin = currentUser?.role === "Operational Admin";
    const isDeptAdmin = currentUser?.role === "Department Admin";
    const isUser = currentUser?.role === "User";

    // [MODIFIED] Check for granular permission
    const hasUserMgmtPermission = currentUser?.can_manage_users === true;
    const canManage = isGlobalAdmin || isOperationalAdmin || isDeptAdmin || hasUserMgmtPermission;

    const [users, setUsers] = useState([]);
    const [teams, setTeams] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);

    const [form, setForm] = useState({
        name: "",
        email: "",
        password: "",
        role: "User",
        department_id: "",
        teamId: "",
        subTeamIds: [],
        memberships: [],
        can_manage_users: false,
        can_manage_teams: false
    });
    const [selectedFile, setSelectedFile] = useState(null);
    const [removePhoto, setRemovePhoto] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);

    const [searchQuery, setSearchQuery] = useState("");
    const [changePasswordEnabled, setChangePasswordEnabled] = useState(false);
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [updateUserLoading, setUpdateUserLoading] = useState(false);

    const toTitleCase = (str) => {
        if (!str) return "";
        return str.split(' ').map(word =>
            word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        ).join(' ');
    };



    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const { data: usersData, error: usersError } = await supabase
                .from("profiles")
                .select("*")
                .is("deleted_at", null)
                .order("name", { ascending: true });

            if (usersError) throw usersError;

            const { data: membershipsData, error: membershipsError } = await supabase
                .from("user_team_memberships")
                .select("*");

            if (membershipsError) throw membershipsError;

            const combinedUsers = (usersData || []).map(u => ({
                ...u,
                user_team_memberships: (membershipsData || []).filter(m => m.user_id === u.id)
            }));

            setUsers(combinedUsers);

            const { data: teamsData, error: teamsError } = await supabase
                .from("teams")
                .select("id, name, department_id");

            if (teamsError) throw teamsError;
            setTeams(teamsData || []);

            const { data: deptsData, error: deptsError } = await supabase
                .from("departments")
                .select("*")
                .order("name");

            if (deptsError) throw deptsError;
            setDepartments(deptsData || []);
        } catch (err) {
            console.error("Error fetching data:", err);
        } finally {
            setLoading(false);
        }
    };



    const handleSaveUser = async (e) => {
        e.preventDefault();
        if (updateUserLoading) return;

        const effectiveTeamId = isDeptAdmin ? currentUser.team_id : form.teamId;

        if (isOperationalAdmin && form.role === "Global Admin") {
            showAlert("Security Restriction: Operational Admins cannot create or promote users to Global Admin.");
            return;
        }
        if (isOperationalAdmin && form.role === "Operational Admin" && (!editingUser || editingUser.id !== currentUser.id)) {
            showAlert("Security Restriction: Operational Admins cannot create or edit other Operational Admins.");
            return;
        }
        if (isDeptAdmin && (form.role === "Global Admin" || form.role === "Operational Admin")) {
            showAlert(`Security Restriction: Department Admins cannot manage ${form.role}s.`);
            return;
        }
        if (isDeptAdmin && form.role === "Department Admin" && (!editingUser || editingUser.id !== currentUser.id)) {
            showAlert("Security Restriction: Department Admins cannot create or edit other Department Admins.");
            return;
        }

        if (isDeptAdmin) {
            const selfUser = users.find(u => u.id === currentUser.id);
            const myDeptId = selfUser?.department_id || currentUser.department_id || teams.find(t => t.id === currentUser.team_id)?.department_id;
            if (form.department_id && myDeptId && form.department_id !== myDeptId) {
                showAlert("Security Restriction: You cannot assign users to other departments.");
                return;
            }
        }

        setUpdateUserLoading(true);
        try {
            let photoUrl = editingUser?.photo_url || null;

            if (selectedFile) {
                const fileExt = selectedFile.name.split(".").pop();
                const fileName = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}.${fileExt}`;
                const { error: uploadError } = await supabase.storage
                    .from("avatars")
                    .upload(fileName, selectedFile);

                if (uploadError) throw uploadError;

                const { data: publicUrlData } = supabase.storage
                    .from("avatars")
                    .getPublicUrl(fileName);

                photoUrl = publicUrlData.publicUrl;
            }

            if (editingUser) {
                if (changePasswordEnabled) {
                    if (!newPassword || newPassword.length < 6) {
                        showAlert("New password must be at least 6 characters.");
                        return;
                    }
                    if (newPassword !== confirmPassword) {
                        showAlert("Passwords do not match.");
                        return;
                    }

                    try {
                        const response = await fetch(`/api/auth/update-password`, {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ userId: editingUser.id, newPassword })
                        });
                        
                        const text = await response.text();
                        let result;
                        try {
                            result = JSON.parse(text);
                        } catch (e) {
                            throw new Error(`${text.slice(0, 50)}... is not valid JSON`);
                        }

                        if (!response.ok) {
                            throw new Error(result.error || result.message || "Failed to update password");
                        }
                    } catch (err) {
                        showAlert(`Password update failed: ${err.message}`);
                        return;
                    }
                }

                const teamObj = teams.find(t => t.id === (effectiveTeamId || form.teamId));
                const deptId = form.department_id || teamObj?.department_id;

                const updates = {
                    name: toTitleCase(form.name),
                    email: form.email,
                    role: form.role,
                    department_id: deptId || null,
                    team_id: effectiveTeamId || null,
                    sub_team_ids: form.subTeamIds || [],
                    photo_url: (removePhoto && !selectedFile) ? null : photoUrl,
                    can_manage_users: form.can_manage_users,
                    can_manage_teams: form.can_manage_teams
                };

                if (changePasswordEnabled) {
                    updates.password = newPassword;
                }

                const { error } = await supabase
                    .from("profiles")
                    .update(updates)
                    .eq("id", editingUser.id)
                    .select()
                    .single();

                if (error) throw error;

                // Update memberships
                await supabase
                    .from("user_team_memberships")
                    .delete()
                    .eq("user_id", editingUser.id);

                const membershipRows = (form.memberships || [])
                    .filter(m => m.department_id && m.team_id)
                    .map(m => ({
                        user_id: editingUser.id,
                        department_id: m.department_id,
                        team_id: m.team_id,
                        role: m.role || (form.role === "Department Admin" ? "Department Admin" : "User"),
                        is_department_admin: m.is_department_admin || m.role === "Department Admin"
                    }));

                if (membershipRows.length > 0) {
                    const { error: membershipError } = await supabase
                        .from("user_team_memberships")
                        .insert(membershipRows);

                    if (membershipError) throw membershipError;
                }

                await fetchUsers();
                if (editingUser.id === currentUser.id && refreshUser) {
                    await refreshUser();
                }
                if (changePasswordEnabled) {
                    showAlert("Password updated successfully!");
                } else {
                    showAlert("User profile updated successfully!");
                }
            } else {
                // [MODIFIED] Use a temporary client to create the user effectively "headless"
                // so it doesn't overwrite the current admin's session.
                const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
                const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

                // Create a temporary client that DOES NOT persist the session
                const dummyStorage = {
                    getItem: () => null,
                    setItem: () => { },
                    removeItem: () => { },
                };

                const tempSupabase = createClient(supabaseUrl, supabaseAnonKey, {
                    auth: {
                        persistSession: false,
                        autoRefreshToken: false,
                        detectSessionInUrl: false,
                        storage: dummyStorage
                    }
                });

                const teamObj = teams.find(t => t.id === (effectiveTeamId || form.teamId));
                const deptId = form.department_id || teamObj?.department_id;

                const { data: signUpData, error } = await tempSupabase.auth.signUp({
                    email: form.email,
                    password: form.password,
                    options: {
                        data: {
                            name: toTitleCase(form.name),
                            email: form.email,
                            role: form.role,
                            password: form.password,
                            department_id: deptId || null,
                            team_id: effectiveTeamId || null,
                            sub_team_ids: form.subTeamIds || [],
                            can_manage_users: form.can_manage_users,
                            can_manage_teams: form.can_manage_teams
                        }
                    }
                });

                if (error) throw error;

                const newUserId = signUpData?.user?.id;
                if (newUserId) {
                    const membershipRows = (form.memberships || [])
                        .filter(m => m.department_id && m.team_id)
                        .map(m => ({
                            user_id: newUserId,
                            department_id: m.department_id,
                            team_id: m.team_id,
                            role: m.role || (form.role === "Department Admin" ? "Department Admin" : "User"),
                            is_department_admin: m.is_department_admin || m.role === "Department Admin"
                        }));

                    if (membershipRows.length > 0) {
                        const { error: membershipError } = await supabase
                            .from("user_team_memberships")
                            .insert(membershipRows);

                        if (membershipError) throw membershipError;
                    }
                }

                showAlert("User account created successfully! The new user must confirm their email.");
                // Refresh the list instead of reloading the page
                await fetchUsers();
                if (onRefreshUsers) onRefreshUsers();
            }

            setIsModalOpen(false);
            setForm({ name: "", email: "", password: "", role: "User", department_id: "", teamId: "", subTeamIds: [], memberships: [], can_manage_users: false, can_manage_teams: false });
            setEditingUser(null);
            setSelectedFile(null);
            setRemovePhoto(false);
        } catch (err) {
            console.error("Error saving user:", err);
            showAlert("Failed to save user: " + err.message);
        } finally {
            setUpdateUserLoading(false);
        }
    };

    const handleEditClick = (user) => {

        if (isOperationalAdmin && user.role === "Global Admin") {
            showAlert("Security Restriction: Operational Admins cannot edit Global Admins.");
            return;
        }
        if (isOperationalAdmin && user.role === "Operational Admin" && user.id !== currentUser.id) {
            showAlert("Security Restriction: Operational Admins cannot edit other Operational Admins.");
            return;
        }
        if (isDeptAdmin && (user.role === "Global Admin" || user.role === "Operational Admin")) {
            showAlert(`Security Restriction: Department Admins cannot edit ${user.role}s.`);
            return;
        }
        if (isDeptAdmin && user.role === "Department Admin" && user.id !== currentUser.id) {
            showAlert("Security Restriction: Department Admins cannot edit other Department Admins.");
            return;
        }

        setEditingUser(user);
        setChangePasswordEnabled(false);
        setNewPassword("");
        setConfirmPassword("");
        setForm({
            name: user.name,
            email: user.email,
            password: user.password || "",
            role: user.role,
            department_id: user.department_id || "",
            teamId: user.team_id || "",
            subTeamIds: user.sub_team_ids || [],
            memberships: user.user_team_memberships || [],
            can_manage_users: user.can_manage_users || false,
            can_manage_teams: user.can_manage_teams || false
        });
        setIsModalOpen(true);
    };

    const handleExportExcel = () => {
        if (!users || users.length === 0) {
            showAlert("No users to export.");
            return;
        }

        let usersToExport = users;
        if (isDeptAdmin) {
            const selfUser = users.find(u => u.id === currentUser.id);
            const myTeamId = selfUser?.team_id || currentUser.team_id;

            usersToExport = users.filter(user => {
                if (user.team_id && user.team_id === myTeamId) {
                    return true;
                }
                const myTeam = teams.find(t => t.id === myTeamId);
                if (myTeam && (myTeam.members || []).some(m => m === user.name || m === user.email)) {
                    return true;
                }
                return false;
            });
        }

        if (usersToExport.length === 0) {
            showAlert("No users found for your department to export.");
            return;
        }

        const headers = ["Name", "Email", "Password", "Department", "Assigned Team/Sub-Team", "Additional Access", "Role", "Status"];
        
        const rows = usersToExport.map(u => {
            const teamObj = teams.find(t => t.id === u.team_id);
            const deptId = u.department_id || teamObj?.department_id;
            const deptName = departments.find(d => d.id === deptId)?.name || "-";

            let subTeamsDisplay = [];
            if (u.sub_team_ids && u.sub_team_ids.length > 0) {
                const names = u.sub_team_ids
                    .map(sid => subTeams.find(st => st.id === sid)?.name)
                    .filter(Boolean);
                if (names.length > 0) subTeamsDisplay.push(`Sub-Teams: ${names.join(", ")}`);
            }
            const teamSubTeamText = `${teamObj?.name || "-"}${subTeamsDisplay.length > 0 ? `\n${subTeamsDisplay.join('\n')}` : ''}`;

            const additionalAccess = (u.user_team_memberships || []).map(m => {
                const dName = departments.find(d => d.id === m.department_id)?.name || "Unknown Dept";
                const tName = teams.find(t => t.id === m.team_id)?.name || "Unknown Team";
                return `${dName} - ${tName}`;
            }).join("\n");

            const status = u.deleted_at ? "Inactive" : "Active";

            return [
                u.name || "",
                u.email || "",
                u.password || "",
                deptName,
                teamSubTeamText,
                additionalAccess || "-",
                u.role || "",
                status
            ];
        });

        const wb = XLSX.utils.book_new();
        const wsData = [headers, ...rows];
        const ws = XLSX.utils.aoa_to_sheet(wsData);

        // Define base border style
        const baseBorder = {
            top: { style: "thin", color: { rgb: "E2E8F0" } },
            bottom: { style: "thin", color: { rgb: "E2E8F0" } },
            left: { style: "thin", color: { rgb: "E2E8F0" } },
            right: { style: "thin", color: { rgb: "E2E8F0" } }
        };

        // Get department color map
        const getDeptColor = (deptName) => {
            const name = deptName.toLowerCase();
            if (name.includes("developer")) return "E6F4EA"; // light green
            if (name.includes("solar")) return "FEF7E0"; // light yellow
            if (name.includes("finance")) return "E8F0FE"; // light blue
            if (name.includes("business")) return "F3E8FD"; // light purple
            return "F8FAFC"; // light gray default
        };

        // Apply styles to all cells
        const range = XLSX.utils.decode_range(ws['!ref']);
        for (let R = range.s.r; R <= range.e.r; ++R) {
            for (let C = range.s.c; C <= range.e.c; ++C) {
                const cellRef = XLSX.utils.encode_cell({ c: C, r: R });
                if (!ws[cellRef]) continue;

                if (!ws[cellRef].s) ws[cellRef].s = {};

                // Header styles
                if (R === 0) {
                    ws[cellRef].s = {
                        fill: { fgColor: { rgb: "76AB3F" } },
                        font: { color: { rgb: "FFFFFF" }, bold: true },
                        alignment: { vertical: "center", horizontal: "left" },
                        border: baseBorder
                    };
                } else {
                    // Data rows
                    const deptName = rows[R - 1][3]; // Department is now at index 3 because of Password
                    const bgColor = getDeptColor(deptName);
                    
                    ws[cellRef].s = {
                        fill: { fgColor: { rgb: bgColor } },
                        font: { color: { rgb: "1E293B" } },
                        alignment: { vertical: "center", horizontal: "left", wrapText: true },
                        border: baseBorder
                    };
                }
            }
        }

        // Set column widths
        ws['!cols'] = [
            { wch: 24 }, // Name
            { wch: 32 }, // Email
            { wch: 20 }, // Password
            { wch: 24 }, // Department
            { wch: 30 }, // Team/Sub-Team
            { wch: 40 }, // Additional Access
            { wch: 22 }, // Role
            { wch: 14 }  // Status
        ];

        // Freeze top row
        ws['!views'] = [{ state: 'frozen', ySplit: 1 }];

        XLSX.utils.book_append_sheet(wb, ws, "Users");
        XLSX.writeFile(wb, "Users_Export.xlsx");
    };

    const handleDeleteUser = async () => {
        if (!userToDelete) return;
        const userId = userToDelete.id;

        const targetUser = users.find(u => u.id === userId);
        if (!targetUser) return;


        const isTargetGlobalAdmin = targetUser.role === "Global Admin";
        const isTargetOperationalAdmin = targetUser.role === "Operational Admin";

        if (isOperationalAdmin && isTargetGlobalAdmin) {
            showAlert("Security Restriction: Operational Admins cannot delete Global Admins.");
            return;
        }
        if (isOperationalAdmin && isTargetOperationalAdmin && targetUser.id !== currentUser.id) {
            showAlert("Security Restriction: Operational Admins cannot delete other Operational Admins.");
            return;
        }
        if (isDeptAdmin && (isTargetGlobalAdmin || isTargetOperationalAdmin)) {
            showAlert(`Security Restriction: Department Admins cannot delete ${targetUser.role}s.`);
            return;
        }
        if (isDeptAdmin && targetUser.role === "Department Admin") {
            showAlert("Security Restriction: Department Admins cannot delete Department Admins.");
            return;
        }

        try {
            const { error } = await supabase
                .from("profiles")
                .update({ deleted_at: new Date().toISOString() })
                .eq("id", userId);

            if (error) throw error;
            setUsers(prev => prev.filter(u => u.id !== userId));
            setDeleteModalOpen(false);
            setUserToDelete(null);
        } catch (err) {
            console.error("Error deleting user:", err);
            showAlert("Failed to delete user");
        }
    };

    const handleToggleUserStatus = async (userId, currentStatus) => {
        const targetUser = users.find(u => u.id === userId);
        if (!targetUser) return;

        // Security checks similar to edit/delete
        const isTargetGlobalAdmin = targetUser.role === "Global Admin";
        const isTargetOperationalAdmin = targetUser.role === "Operational Admin";

        if (isOperationalAdmin && isTargetGlobalAdmin) {
            showAlert("Security Restriction: Operational Admins cannot modify Global Admin status.");
            return;
        }
        if (isOperationalAdmin && isTargetOperationalAdmin && targetUser.id !== currentUser.id) {
            showAlert("Security Restriction: Operational Admins cannot modify other Operational Admin status.");
            return;
        }
        if (isDeptAdmin && (isTargetGlobalAdmin || isTargetOperationalAdmin)) {
            showAlert(`Security Restriction: Department Admins cannot modify ${targetUser.role} status.`);
            return;
        }
        if (isDeptAdmin && targetUser.role === "Department Admin") {
            showAlert("Security Restriction: Department Admins cannot modify Department Admin status.");
            return;
        }

        try {
            const newStatus = !currentStatus;
            const { error } = await supabase
                .from("profiles")
                .update({ is_active: newStatus })
                .eq("id", userId);

            if (error) throw error;
            setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_active: newStatus } : u));
            showAlert(`User ${newStatus ? 'activated' : 'deactivated'} successfully!`);
        } catch (err) {
            console.error("Error toggling user status:", err);
            showAlert("Failed to update user status");
        }
    };


    if (!canManage && !isUser) {
        return (
            <div className="page">
                <div className="card" style={{ padding: 24, textAlign: "center", color: "var(--danger-600)" }}>
                    <h3>Access Denied</h3>
                    <p>You do not have permission to view this page.</p>
                </div>
            </div>
        );
    }
    // const selfUser = users.find(u => u.id === currentUser.id);
    // const myTeamId = selfUser?.team_id || currentUser.team_id;

    const filteredUsers = users.filter(user => {
        // [MODIFIED] Team visibility filter temporarily removed -> Re-enabled for Dept Admin
        if (isDeptAdmin) {
            const userDeptId = user.department_id || teams.find(t => t.id === user.team_id)?.department_id;
            const myDeptId = currentUser.department_id || teams.find(t => t.id === currentUser.team_id)?.department_id;

            // If we can't resolve departments, default to strictly matching team or loose checking
            if (myDeptId && userDeptId !== myDeptId) {
                return false;
            }
        }

        return (user.name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
            (user.email || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
            (user.department || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
            (user.role || "").toLowerCase().includes(searchQuery.toLowerCase());
    });

    return (
        <div className={`page ${darkMode ? 'dark bg-gray-900' : ''}`}>
            <div className="page-header" style={{
                background: darkMode ? "#4d7a22" : "#76AB3F",
                padding: "28px 34px",
                borderRadius: "30px",
                color: "white",
                boxShadow: darkMode ? "0 10px 25px -5px rgba(0, 0, 0, 0.4)" : "0 10px 25px -5px rgba(16, 185, 129, 0.25)",
                display: "flex",
                alignItems: "center",
                gap: 12,
                flexWrap: "nowrap",
                overflow: "visible",
                marginBottom: 24,
                minHeight: 88,
                transition: "all 0.3s ease"
            }}>
                <h2
                    style={{
                        color: "white",
                        margin: 0,
                        fontWeight: 900,
                        fontSize: "20px",
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                        letterSpacing: "0.0px",
                    }}
                >
                    User Management
                </h2>

                <div className="users-header-row" style={{ display: "flex", alignItems: "center", width: "100%", marginLeft: "12px" }}>
                    <input id="searchUsers" name="searchUsers"
                        type="text"
                        placeholder="Search users..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={{
                            width: 240,
                            padding: "10px 14px",
                            borderRadius: 8,
                            border: "none",
                            outline: "none",
                            background: darkMode ? "rgba(255,255,255,0.15)" : "white",
                            color: darkMode ? "white" : "#1f2937",
                            boxShadow: "0 1px 2px rgba(0,0,0,0.05)"
                        }}
                    />
                    <div style={{ flex: 1 }} className="users-header-spacer"></div>
                    <div className="users-header-actions" style={{ display: "flex", gap: 12 }}>
                        {canManage && <button className="btn-secondary" onClick={handleExportExcel} style={{ background: "white", color: "#76AB3F", border: "none", fontWeight: 600, padding: "10px 16px", borderRadius: "8px", boxShadow: "0 1px 2px rgba(0,0,0,0.05)" }}>Export Excel</button>}
                        {canManage && <button className="btn-secondary" onClick={() => {
                            setEditingUser(null);
                            setChangePasswordEnabled(false);
                            setNewPassword("");
                            setConfirmPassword("");
                            const selfUser = users.find(u => u.id === currentUser.id);
                            const myDeptId = selfUser?.department_id || currentUser.department_id || teams.find(t => t.id === currentUser.team_id)?.department_id;

                            if (isDeptAdmin && !myDeptId) {
                                showAlert("Configuration Error: Your account is not correctly assigned to a department. Please contact support.");
                                return;
                            }

                            setForm({
                                name: "",
                                email: "",
                                password: "",
                                role: "User",
                                department_id: isDeptAdmin ? myDeptId : "",
                                teamId: isDeptAdmin ? currentUser.team_id : "",
                                subTeamIds: [],
                                memberships: [],
                                can_manage_users: false,
                                can_manage_teams: false
                            });
                            setIsModalOpen(true);
                        }} style={{ background: "white", color: "#76AB3F", border: "none", fontWeight: 600, padding: "10px 16px", borderRadius: "8px", boxShadow: "0 1px 2px rgba(0,0,0,0.05)" }}>+ New User</button>}
                    </div>
                </div>
            </div>

            <div className="card" style={{ padding: 0, overflowX: "auto", width: "100%", background: darkMode ? "#1f2937" : "white", border: darkMode ? "1px solid #374151" : "none" }}>
                {loading ? (
                    <div style={{ padding: 24, textAlign: "center", color: darkMode ? "#9ca3af" : "var(--gray-500)" }}>Loading users...</div>
                ) : (
                    <table className="modern-table" style={{ minWidth: "1200px", tableLayout: "fixed" }}>
                        <thead>
                            <tr style={{ background: "transparent" }}>
                                <th style={{ width: "12%", color: darkMode ? "#9ca3af" : "var(--gray-500)", borderBottom: darkMode ? "1px solid #374151" : "1px solid var(--gray-200)" }}>Name</th>
                                <th style={{ width: "18%", color: darkMode ? "#9ca3af" : "var(--gray-500)", borderBottom: darkMode ? "1px solid #374151" : "1px solid var(--gray-200)" }}>Email</th>
                                <th style={{ width: "12%", color: darkMode ? "#9ca3af" : "var(--gray-500)", borderBottom: darkMode ? "1px solid #374151" : "1px solid var(--gray-200)" }}>Department</th>
                                <th style={{ width: "16%", color: darkMode ? "#9ca3af" : "var(--gray-500)", borderBottom: darkMode ? "1px solid #374151" : "1px solid var(--gray-200)" }}>Assigned Team/Sub-team</th>
                                <th style={{ width: "18%", color: darkMode ? "#9ca3af" : "var(--gray-500)", borderBottom: darkMode ? "1px solid #374151" : "1px solid var(--gray-200)" }}>Additional Access</th>
                                <th style={{ width: "10%", color: darkMode ? "#9ca3af" : "var(--gray-500)", borderBottom: darkMode ? "1px solid #374151" : "1px solid var(--gray-200)", textAlign: "center" }}>Role</th>
                                <th style={{ width: "6%", color: darkMode ? "#9ca3af" : "var(--gray-500)", borderBottom: darkMode ? "1px solid #374151" : "1px solid var(--gray-200)", textAlign: "center" }}>Status</th>
                                {canManage && <th style={{ width: "8%", color: darkMode ? "#9ca3af" : "var(--gray-500)", borderBottom: darkMode ? "1px solid #374151" : "1px solid var(--gray-200)", textAlign: "center" }}>Actions</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {filteredUsers.map(user => {
                                const teamObj = teams.find(t => t.id === user.team_id);
                                const teamName = teamObj?.name || "-";

                                // Resolve Department
                                const deptId = user.department_id || teamObj?.department_id;
                                const deptName = departments.find(d => d.id === deptId)?.name || "-";

                                  const assignedSubTeams = subTeams.filter(st => (user.sub_team_ids || []).includes(st.id));
                                  const subTeamNames = assignedSubTeams.map(st => st.name).join(", ");
                                  const displayTeam = subTeamNames ? `${teamName} / ${subTeamNames}` : teamName;

                                  const memberships = user.user_team_memberships || [];
                                  const accessBadges = memberships.map(m => {
                                      const dept = departments.find(d => d.id === m.department_id)?.name || "-";
                                      const team = teams.find(t => t.id === m.team_id)?.name || "-";
                                      const mRole = m.role || (m.is_department_admin ? "Department Admin" : "User");
                                      return `${dept} / ${team} (${mRole})`;
                                  });

                                  return (
                                      <tr
                                          key={user.id}
                                          onClick={() => canManage && handleEditClick(user)}
                                          style={{ cursor: canManage ? "pointer" : "default", borderBottom: darkMode ? "1px solid #374151" : "1px solid var(--gray-100)" }}
                                          className="user-row"
                                      >
                                          <td style={{ fontWeight: 500, color: darkMode ? "#f9fafb" : "var(--gray-900)", wordBreak: "break-word", whiteSpace: "normal" }}>{user.name}</td>
                                          <td style={{ color: darkMode ? "#d1d5db" : "var(--gray-600)", wordBreak: "break-word", whiteSpace: "normal" }}>{user.email}</td>
                                          <td style={{ color: darkMode ? "#d1d5db" : "var(--gray-600)", wordBreak: "break-word", whiteSpace: "normal" }}>{deptName}</td>
                                          <td style={{ color: darkMode ? "#d1d5db" : "var(--gray-600)", wordBreak: "break-word", whiteSpace: "normal" }}>{displayTeam}</td>
                                          <td style={{ color: darkMode ? "#d1d5db" : "var(--gray-600)", whiteSpace: "normal" }}>
                                              {accessBadges.length ? (
                                                  <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                                                      {accessBadges.map((badge, idx) => (
                                                          <span key={idx} className="priority-pill priority-Low" style={{
                                                              fontSize: 11,
                                                              background: darkMode ? "#1e293b" : "#f1f5f9",
                                                              color: darkMode ? "#4ade80" : "#166534",
                                                              padding: "2px 6px",
                                                              borderRadius: "4px",
                                                              width: "fit-content",
                                                              border: darkMode ? "1px solid #10b981" : "1px solid #76AB3F",
                                                              whiteSpace: "normal",
                                                              textAlign: "left",
                                                              wordBreak: "break-word"
                                                          }}>
                                                              {badge}
                                                          </span>
                                                      ))}
                                                  </div>
                                              ) : (
                                                  "No additional access"
                                              )}
                                          </td>
                                        <td style={{ textAlign: "center" }}>
                                            <span className={`priority-pill ${user.role === 'Global Admin' ? 'priority-High' : user.role === 'Operational Admin' ? 'priority-Medium' : user.role === 'Department Admin' ? 'priority-Medium' : 'priority-Low'}`}
                                                style={{
                                                    fontSize: 12,
                                                    background: user.role === "Global Admin" ? "#fee2e2" : user.role === "Operational Admin" ? "#e0e7ff" : (darkMode ? "#1e3a24" : "#f0fdf4"),
                                                    color: user.role === "Global Admin" ? "#b91c1c" : user.role === "Operational Admin" ? "#4338ca" : (darkMode ? "#4ade80" : "#166534"),
                                                    whiteSpace: "normal",
                                                    textAlign: "center"
                                                }}>
                                                {user.role}
                                            </span>
                                        </td>
                                        {/* Toggle Switch Icon for Active/Inactive */}
                                        <td style={{ textAlign: "center" }}>
                                            <div
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (canManage) {
                                                        handleToggleUserStatus(user.id, user.is_active !== false);
                                                    }
                                                }}
                                                style={{
                                                    cursor: canManage ? "pointer" : "not-allowed",
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    opacity: canManage ? 1 : 0.5
                                                }}
                                                title={user.is_active !== false ? "Active - Click to deactivate" : "Inactive - Click to activate"}
                                            >
                                                <svg
                                                    width="36"
                                                    height="20"
                                                    viewBox="0 0 36 20"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    style={{
                                                        transition: "all 0.3s ease"
                                                    }}
                                                >
                                                    {/* Background track */}
                                                    <rect
                                                        x="2"
                                                        y="2"
                                                        width="32"
                                                        height="16"
                                                        rx="8"
                                                        fill={user.is_active !== false ? (darkMode ? "#1e3a24" : "#dcfce7") : (darkMode ? "#3f1f1f" : "#fee2e2")}
                                                        stroke={user.is_active !== false ? (darkMode ? "#4ade80" : "#166534") : (darkMode ? "#f87171" : "#b91c1c")}
                                                        strokeWidth="2"
                                                    />
                                                    {/* Toggle circle */}
                                                    <circle
                                                        cx={user.is_active !== false ? "26" : "10"}
                                                        cy="10"
                                                        r="6"
                                                        fill={user.is_active !== false ? (darkMode ? "#4ade80" : "#166534") : (darkMode ? "#f87171" : "#b91c1c")}
                                                        style={{
                                                            transition: "cx 0.3s ease"
                                                        }}
                                                    />
                                                </svg>
                                            </div>
                                        </td>
                                        {canManage && (
                                            <td style={{ textAlign: "center" }}>
                                                <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
                                                    {(() => {
                                                        const isTargetGlobalAdmin = user.role === "Global Admin";
                                                        const isTargetOperationalAdmin = user.role === "Operational Admin";

                                                        let canEdit = true;
                                                        let canDelete = true;

                                                        if (isOperationalAdmin && (isTargetGlobalAdmin || (isTargetOperationalAdmin && user.id !== currentUser.id))) {
                                                            canEdit = false;
                                                            canDelete = false;
                                                        }
                                                        if (isDeptAdmin && (isTargetGlobalAdmin || isTargetOperationalAdmin || (user.role === "Department Admin" && user.id !== currentUser.id))) {
                                                            canEdit = false;
                                                            canDelete = false;
                                                        }

                                                        return (
                                                            <>
                                                                <button
                                                                    className="btn-secondary"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleEditClick(user);
                                                                    }}
                                                                    disabled={!canEdit}
                                                                    style={{
                                                                        padding: "4px 8px",
                                                                        fontSize: 12,
                                                                        opacity: canEdit ? 1 : 0.4,
                                                                        cursor: canEdit ? "pointer" : "not-allowed"
                                                                    }}
                                                                >
                                                                    Edit
                                                                </button>
                                                                <button
                                                                    className="btn-danger"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setUserToDelete(user);
                                                                        setDeleteModalOpen(true);
                                                                    }}
                                                                    disabled={!canDelete || user.id === currentUser.id}
                                                                    style={{
                                                                        opacity: (canDelete && user.id !== currentUser.id) ? 1 : 0.4,
                                                                        padding: "4px 8px",
                                                                        fontSize: 12,
                                                                        cursor: (canDelete && user.id !== currentUser.id) ? "pointer" : "not-allowed"
                                                                    }}
                                                                >
                                                                    Delete
                                                                </button>
                                                            </>
                                                        );
                                                    })()}
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                )
                            })}
                            {filteredUsers.length === 0 && (
                                <tr>
                                     <td colSpan="8" style={{ textAlign: "center", padding: 24, color: darkMode ? "#9ca3af" : "var(--gray-500)", fontStyle: "italic" }}>
                                        No users found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                )}
            </div>

            {
                isModalOpen && (
                    <div className="modal-backdrop"
                        onClick={() => {
                            setIsModalOpen(false);
                            setEditingUser(null);
                            setForm({ name: "", email: "", password: "", role: "User", department_id: "", teamId: "", subTeamIds: [], memberships: [], can_manage_users: false, can_manage_teams: false });
                        }}
                        style={{ background: "rgba(17, 24, 39, 0.7)", backdropFilter: "blur(8px)" }}>
                        <div className="modal-card" onClick={e => e.stopPropagation()} style={{
                            maxWidth: "680px",
                            padding: 0,
                            overflow: "hidden",
                            borderRadius: "24px",
                            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                            maxHeight: "92vh",
                            display: "flex",
                            flexDirection: "column",
                            background: darkMode ? "#1f2937" : "white"
                        }}>
                            {/* Header */}
                            <div className="modal-header" style={{
                                background: "#76AB3F",
                                margin: 0,
                                padding: "24px 32px",
                                borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between"
                            }}>
                                <div>
                                    <h3 style={{ color: "white", margin: 0, fontSize: "20px", fontWeight: 700 }}>
                                        {editingUser ? "Edit User Account" : "Create New Account"}
                                    </h3>
                                    <p style={{ margin: "4px 0 0", fontSize: "13px", color: "rgba(255, 255, 255, 0.8)" }}>
                                        {editingUser ? "Update user profile and team assignments" : "Set up a new user and assign them to a department"}
                                    </p>
                                </div>
                                <button className="modal-close" onClick={() => {
                                    setIsModalOpen(false);
                                    setEditingUser(null);
                                    setForm({ name: "", email: "", password: "", role: "User", department_id: "", teamId: "", subTeamIds: [], memberships: [], can_manage_users: false, can_manage_teams: false });
                                }} 
                                disabled={updateUserLoading}
                                style={{
                                    color: "white",
                                    background: "rgba(255, 255, 255, 0.15)",
                                    width: "32px",
                                    height: "32px",
                                    borderRadius: "50%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    transition: "all 0.2s",
                                    opacity: updateUserLoading ? 0.6 : 1,
                                    cursor: updateUserLoading ? "not-allowed" : "pointer"
                                }}>✕</button>
                            </div>

                            <div style={{ padding: "20px 32px 32px", background: darkMode ? "#111827" : "white", borderTop: darkMode ? "1px solid #374151" : "none", overflowY: "auto", flex: 1 }}>
                                <form onSubmit={handleSaveUser}>
                                    <fieldset disabled={updateUserLoading} style={{ border: "none", padding: 0, margin: 0 }}>
                                    <div style={{
                                        display: "grid",
                                        gridTemplateColumns: "1fr 1fr",
                                        gap: "16px",
                                        marginBottom: "20px"
                                    }}>
                                        {/* Column 1: Identity */}
                                        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                                            <div className="form-group" style={{ margin: 0 }}>
                                                <label style={{ color: darkMode ? "#d1d5db" : "var(--gray-700)", fontWeight: 600 }}>Full Name <span className="required">*</span></label>
                                                <input id="name" name="name"
                                                    required
                                                    value={form.name}
                                                    onChange={e => setForm({ ...form, name: e.target.value })}
                                                    placeholder="e.g. John Doe"
                                                    style={{ padding: "10px 14px", fontSize: "15px", background: darkMode ? "#374151" : "white", color: darkMode ? "white" : "inherit", border: darkMode ? "1px solid #4b5563" : "1px solid var(--gray-200)" }}
                                                />
                                            </div>
                                            <div className="form-group" style={{ margin: 0 }}>
                                                <label style={{ color: darkMode ? "#d1d5db" : "var(--gray-700)", fontWeight: 600 }}>Email Address <span className="required">*</span></label>
                                                <input id="email" name="email"
                                                    type="email"
                                                    required
                                                    value={form.email}
                                                    onChange={e => setForm({ ...form, email: e.target.value })}
                                                    placeholder="john@example.com"
                                                    style={{ padding: "10px 14px", fontSize: "15px", background: darkMode ? "#374151" : "white", color: darkMode ? "white" : "inherit", border: darkMode ? "1px solid #4b5563" : "1px solid var(--gray-200)" }}
                                                />
                                            </div>
                                            <div className="form-group" style={{ margin: 0 }}>
                                                <label style={{ color: darkMode ? "#d1d5db" : "var(--gray-700)", fontWeight: 600 }}>Access Role</label>
                                                <CustomSelect
                                                    options={[
                                                        { value: "User", label: "Regular User" },
                                                        ...(isDeptAdmin ? [] : [{ value: "Department Admin", label: "Department Admin" }]),
                                                        ...(isGlobalAdmin ? [{ value: "Operational Admin", label: "Operational Admin" }] : []),
                                                        ...(isGlobalAdmin ? [{ value: "Global Admin", label: "Global Admin" }] : [])
                                                    ]}
                                                    value={form.role}
                                                    onChange={val => setForm({ ...form, role: val })}
                                                    placeholder="Select Role..."
                                                    darkMode={darkMode}
                                                />
                                            </div>



                                            {/* Granular Permissions */}
                                            {form.role === "User" && (
                                                <div style={{ marginTop: 0 }}>
                                                    <label style={{ color: darkMode ? "#d1d5db" : "var(--gray-700)", fontWeight: 600, display: "block", marginBottom: "8px" }}>Additional Permissions</label>
                                                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                                        <label style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer", fontSize: "14px", color: darkMode ? "#e5e7eb" : "var(--gray-700)" }}>
                                                            <input id="canmanageusers" name="canmanageusers"
                                                                type="checkbox"
                                                                checked={form.can_manage_users}
                                                                onChange={e => setForm({ ...form, can_manage_users: e.target.checked })}
                                                                style={{ width: "16px", height: "16px", accentColor: "#76AB3F" }}
                                                            />
                                                            Can Manage Users
                                                        </label>
                                                        <label style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer", fontSize: "14px", color: darkMode ? "#e5e7eb" : "var(--gray-700)" }}>
                                                            <input id="canmanageteams" name="canmanageteams"
                                                                type="checkbox"
                                                                checked={form.can_manage_teams}
                                                                onChange={e => setForm({ ...form, can_manage_teams: e.target.checked })}
                                                                style={{ width: "16px", height: "16px", accentColor: "#76AB3F" }}
                                                            />
                                                            Can Manage Teams
                                                        </label>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Column 2: Security & Photo */}
                                        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                                            {!editingUser ? (
                                                <div className="form-group" style={{ margin: 0 }}>
                                                    <label style={{ color: darkMode ? "#d1d5db" : "var(--gray-700)", fontWeight: 600 }}>Password <span className="required">*</span></label>
                                                    <input id="password" name="password"
                                                        type="text"
                                                        required
                                                        value={form.password}
                                                        onChange={e => setForm({ ...form, password: e.target.value })}
                                                        placeholder="••••••••"
                                                        style={{ padding: "10px 14px", fontSize: "15px", fontFamily: "monospace", background: darkMode ? "#374151" : "white", color: darkMode ? "white" : "inherit", border: darkMode ? "1px solid #4b5563" : "1px solid var(--gray-200)" }}
                                                    />
                                                </div>
                                            ) : (
                                                <div className="form-group" style={{ margin: 0, padding: "16px", borderRadius: "12px", border: darkMode ? "1px solid #374151" : "1px solid var(--gray-200)", background: darkMode ? "rgba(255,255,255,0.02)" : "#fafafa" }}>
                                                    <label style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer", fontSize: "15px", fontWeight: 600, color: darkMode ? "#e5e7eb" : "var(--gray-700)", marginBottom: changePasswordEnabled ? "12px" : "0" }}>
                                                        <input 
                                                            type="checkbox"
                                                            checked={changePasswordEnabled}
                                                            onChange={e => {
                                                                setChangePasswordEnabled(e.target.checked);
                                                                if (!e.target.checked) {
                                                                    setNewPassword("");
                                                                    setConfirmPassword("");
                                                                }
                                                            }}
                                                            style={{ width: "16px", height: "16px", accentColor: "#76AB3F" }}
                                                        />
                                                        Change Password
                                                    </label>

                                                    {changePasswordEnabled && (
                                                        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                                                            <div>
                                                                <label style={{ fontSize: "13px", color: darkMode ? "#9ca3af" : "var(--gray-500)", marginBottom: "4px", display: "block" }}>New Password <span className="required">*</span></label>
                                                                <input 
                                                                    type="text"
                                                                    required={changePasswordEnabled}
                                                                    value={newPassword}
                                                                    onChange={e => setNewPassword(e.target.value)}
                                                                    placeholder="••••••••"
                                                                    style={{ width: "100%", padding: "8px 12px", fontSize: "14px", fontFamily: "monospace", background: darkMode ? "#374151" : "white", color: darkMode ? "white" : "inherit", border: darkMode ? "1px solid #4b5563" : "1px solid var(--gray-200)", borderRadius: "6px" }}
                                                                />
                                                            </div>
                                                            <div>
                                                                <label style={{ fontSize: "13px", color: darkMode ? "#9ca3af" : "var(--gray-500)", marginBottom: "4px", display: "block" }}>Confirm Password <span className="required">*</span></label>
                                                                <input 
                                                                    type="text"
                                                                    required={changePasswordEnabled}
                                                                    value={confirmPassword}
                                                                    onChange={e => setConfirmPassword(e.target.value)}
                                                                    placeholder="••••••••"
                                                                    style={{ width: "100%", padding: "8px 12px", fontSize: "14px", fontFamily: "monospace", background: darkMode ? "#374151" : "white", color: darkMode ? "white" : "inherit", border: darkMode ? "1px solid #4b5563" : "1px solid var(--gray-200)", borderRadius: "6px" }}
                                                                />
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            {!editingUser && (
                                                <div className="form-group" style={{ margin: 0 }}>
                                                    <label style={{ color: darkMode ? "#d1d5db" : "var(--gray-700)", fontWeight: 600 }}>Profile Photo</label>
                                                    <div style={{
                                                        border: darkMode ? "2px dashed #4b5563" : "2px dashed var(--gray-200)",
                                                        borderRadius: "12px",
                                                        padding: "16px",
                                                        textAlign: "center",
                                                        background: darkMode ? "#1f2937" : "var(--gray-50)",
                                                        position: "relative"
                                                    }}>
                                                        <input id="fileInput-1" name="fileInput-1"
                                                            type="file"
                                                            accept="image/*"
                                                            onChange={(e) => setSelectedFile(e.target.files[0])}
                                                            style={{
                                                                position: "absolute",
                                                                inset: 0,
                                                                opacity: 0,
                                                                cursor: "pointer"
                                                            }}
                                                        />
                                                        <div style={{ fontSize: "13px", color: darkMode ? "#9ca3af" : "var(--gray-500)" }}>
                                                            {selectedFile ? selectedFile.name : "Click to upload avatar"}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                            <div className="form-group" style={{ margin: 0 }}>
                                                <label style={{ color: darkMode ? "#d1d5db" : "var(--gray-700)", fontWeight: 600 }}>Assigned Department</label>
                                                <CustomSelect
                                                    options={departments.map(d => ({ value: d.id, label: d.name }))}
                                                    value={form.department_id}
                                                    onChange={val => setForm({ ...form, department_id: val, teamId: "" })}
                                                    placeholder="Select Department..."
                                                    disabled={isDeptAdmin}
                                                    darkMode={darkMode}
                                                />
                                            </div>
                                            <div className="form-group" style={{ margin: 0 }}>
                                                <label style={{ color: darkMode ? "#d1d5db" : "var(--gray-700)", fontWeight: 600 }}>Assigned Team</label>
                                                <CustomSelect
                                                    options={teams
                                                        .filter(t => !form.department_id || t.department_id === form.department_id)
                                                        .map(t => ({ value: t.id, label: t.name }))
                                                    }
                                                    value={form.teamId}
                                                    onChange={val => {
                                                        const selectedTeam = teams.find(t => t.id === val);
                                                        setForm({
                                                            ...form,
                                                            teamId: val,
                                                            department_id: selectedTeam?.department_id || form.department_id,
                                                            subTeamIds: []
                                                        });
                                                    }}
                                                    disabled={isDeptAdmin}
                                                    placeholder="Select Team..."
                                                    darkMode={darkMode}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Full Width: Sub-teams Interactive Cards */}
                                    {form.teamId && (
                                        <div className="form-group" style={{ marginBottom: "24px" }}>
                                            <label style={{ color: darkMode ? "#d1d5db" : "var(--gray-700)", fontWeight: 600, display: "block", marginBottom: "12px" }}>
                                                Assigned Sub-teams <span style={{ fontWeight: 400, color: darkMode ? "#6b7280" : "var(--gray-400)", fontSize: "12px" }}>(Select multiple)</span>
                                            </label>
                                            <div style={{
                                                display: "grid",
                                                gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
                                                gap: "10px",
                                                maxHeight: "200px",
                                                overflowY: "auto",
                                                padding: "4px"
                                            }}>
                                                {subTeams.filter(st => st.team_id === form.teamId).map(st => {
                                                    const mt_isSelected = (form.subTeamIds || []).includes(st.id);
                                                    return (
                                                        <div
                                                            key={st.id}
                                                            onClick={() => {
                                                                const current = form.subTeamIds || [];
                                                                const next = mt_isSelected ? current.filter(id => id !== st.id) : [...current, st.id];
                                                                setForm({ ...form, subTeamIds: next });
                                                            }}
                                                            style={{
                                                                padding: "12px",
                                                                borderRadius: "16px",
                                                                cursor: "pointer",
                                                                background: mt_isSelected ? (darkMode ? "#064e3b" : "#f0fdf4") : (darkMode ? "#1f2937" : "white"),
                                                                border: `2px solid ${mt_isSelected ? "#76AB3F" : (darkMode ? "#374151" : "var(--gray-200)")}`,
                                                                transition: "all 0.2s"
                                                            }}>
                                                            <div style={{ fontWeight: 700, fontSize: "14px", color: mt_isSelected ? "#76AB3F" : (darkMode ? "#d1d5db" : "var(--gray-700)") }}>{st.name}</div>
                                                            <div style={{ fontSize: "11px", color: mt_isSelected ? "#76AB3F" : (darkMode ? "#9ca3af" : "var(--gray-400)") }}>Sub-team</div>
                                                        </div>
                                                    )
                                                })}
                                                {subTeams.filter(st => st.team_id === form.teamId).length === 0 && (
                                                    <div style={{ gridColumn: "1/-1", textAlign: "center", padding: "20px", color: "var(--gray-400)", fontSize: "13px", fontStyle: "italic" }}>
                                                        No sub-teams available for this department.
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* Memberships Row Editor Section */}
                                    <div style={{ marginTop: "24px", marginBottom: "24px", paddingBottom: "12px", borderBottom: darkMode ? "1px solid #374151" : "1px solid var(--gray-100)" }}>
                                        <label style={{ color: darkMode ? "#d1d5db" : "var(--gray-700)", fontWeight: 700, display: "flex", alignItems: "center", gap: "8px", fontSize: "14px" }}>
                                            <span style={{ width: "4px", height: "16px", background: "#76AB3F", display: "inline-block", borderRadius: "2px" }}></span>
                                            ADDITIONAL DEPARTMENT & TEAM MEMBERSHIPS
                                        </label>
                                    </div>
                                    <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginBottom: "24px" }}>
                                        {(form.memberships || []).map((membership, index) => (
                                            <div key={index} style={{ display: "grid", gridTemplateColumns: "1.2fr 1.2fr 1fr auto", gap: "10px", alignItems: "center" }}>
                                                {/* Department Select */}
                                                <CustomSelect
                                                    options={departments.map(d => ({ value: d.id, label: d.name }))}
                                                    value={membership.department_id}
                                                    onChange={val => {
                                                        const updated = [...form.memberships];
                                                        updated[index] = { ...updated[index], department_id: val, team_id: "" };
                                                        setForm({ ...form, memberships: updated });
                                                    }}
                                                    placeholder="Select Department..."
                                                    darkMode={darkMode}
                                                />
                                                {/* Team Select */}
                                                <CustomSelect
                                                    options={teams
                                                        .filter(t => t.department_id === membership.department_id)
                                                        .map(t => ({ value: t.id, label: t.name }))}
                                                    value={membership.team_id}
                                                    onChange={val => {
                                                        const updated = [...form.memberships];
                                                        updated[index] = { ...updated[index], team_id: val };
                                                        setForm({ ...form, memberships: updated });
                                                    }}
                                                    placeholder="Select Team..."
                                                    disabled={!membership.department_id}
                                                    darkMode={darkMode}
                                                />
                                                {/* Role Select */}
                                                <CustomSelect
                                                    options={[
                                                        { value: "User", label: "User" },
                                                        { value: "Department Admin", label: "Department Admin" }
                                                    ]}
                                                    value={membership.role || "User"}
                                                    onChange={val => {
                                                        const updated = [...form.memberships];
                                                        updated[index] = { 
                                                            ...updated[index], 
                                                            role: val,
                                                            is_department_admin: val === "Department Admin"
                                                        };
                                                        setForm({ ...form, memberships: updated });
                                                    }}
                                                    placeholder="Select Role..."
                                                    darkMode={darkMode}
                                                />
                                                {/* Remove Button */}
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const updated = form.memberships.filter((_, i) => i !== index);
                                                        setForm({ ...form, memberships: updated });
                                                    }}
                                                    style={{
                                                        padding: "8px 12px",
                                                        background: "#fee2e2",
                                                        color: "#ef4444",
                                                        border: "none",
                                                        borderRadius: "12px",
                                                        fontWeight: 600,
                                                        cursor: "pointer"
                                                    }}
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        ))}

                                        <button
                                            type="button"
                                            onClick={() => {
                                                const updated = [
                                                    ...(form.memberships || []),
                                                    { department_id: "", team_id: "", role: "User", is_department_admin: false }
                                                ];
                                                setForm({ ...form, memberships: updated });
                                            }}
                                            style={{
                                                alignSelf: "flex-start",
                                                padding: "8px 16px",
                                                background: darkMode ? "#1e293b" : "#f0fdf4",
                                                color: "#76AB3F",
                                                border: `1px dashed #76AB3F`,
                                                borderRadius: "12px",
                                                fontWeight: 600,
                                                cursor: "pointer",
                                                marginTop: "8px"
                                            }}
                                        >
                                            + Add Membership
                                        </button>
                                    </div>

                                    {/* Modal Actions */}
                                    <div style={{
                                        display: "flex",
                                        justifyContent: "flex-end",
                                        gap: "12px",
                                        paddingTop: "24px",
                                        borderTop: "1px solid var(--gray-100)"
                                    }}>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setIsModalOpen(false);
                                                setEditingUser(null);
                                                setForm({ name: "", email: "", password: "", role: "User", department_id: "", teamId: "", subTeamIds: [], memberships: [] });
                                            }}
                                            style={{
                                                padding: "10px 24px",
                                                borderRadius: "12px",
                                                background: "var(--gray-50)",
                                                color: "var(--gray-700)",
                                                fontWeight: 600,
                                                opacity: updateUserLoading ? 0.6 : 1,
                                                cursor: updateUserLoading ? "not-allowed" : "pointer"
                                            }}
                                            disabled={updateUserLoading}
                                        >Cancel</button>
                                        <button
                                            type="submit"
                                            disabled={updateUserLoading}
                                            style={{
                                                padding: "10px 32px",
                                                borderRadius: "12px",
                                                background: "#76AB3F",
                                                color: "white",
                                                fontWeight: 600,
                                                boxShadow: "0 4px 14px rgba(118, 171, 63, 0.3)",
                                                opacity: updateUserLoading ? 0.6 : 1,
                                                cursor: updateUserLoading ? "not-allowed" : "pointer"
                                            }}
                                        >
                                            {updateUserLoading ? (editingUser ? "Updating user..." : "Creating user...") : (editingUser ? "Update User" : "Create User")}
                                        </button>
                                    </div>
                                    </fieldset>
                                </form>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Delete Confirmation Modal - Moved outside to work correctly */}
            {
                deleteModalOpen && (
                    <div className="modal-backdrop" onClick={() => setDeleteModalOpen(false)} style={{ background: "rgba(17, 24, 39, 0.7)", backdropFilter: "blur(4px)" }}>
                        <div className="modal-card" onClick={e => e.stopPropagation()} style={{
                            maxWidth: "400px",
                            padding: "24px",
                            borderRadius: "16px",
                            background: darkMode ? "#1f2937" : "white",
                            border: darkMode ? "1px solid #374151" : "none",
                            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)"
                        }}>
                            <h3 style={{ margin: "0 0 12px", fontSize: "18px", fontWeight: 600, color: darkMode ? "#f9fafb" : "#111827" }}>Confirm Deletion</h3>
                            <p style={{ margin: "0 0 24px", color: darkMode ? "#9ca3af" : "#6b7280", fontSize: "14px" }}>
                                Are you sure you want to delete <strong>{userToDelete?.name}</strong>? This action cannot be undone.
                            </p>
                            <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}>
                                <button
                                    className="btn-secondary"
                                    onClick={() => setDeleteModalOpen(false)}
                                    style={{ padding: "8px 16px", fontSize: "14px", background: darkMode ? "#374151" : "#f3f4f6", color: darkMode ? "#e5e7eb" : "#374151", border: "none" }}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="btn-danger"
                                    onClick={handleDeleteUser}
                                    style={{ padding: "8px 16px", fontSize: "14px" }}
                                >
                                    Delete User
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }
        </div >
    );
}

export default Users;
